/**
 * PDF review viewer — shared between reviewers (create/edit annotations)
 * and authors (read/resolve/delete annotations).
 *
 * Annotation coordinates are stored in the PDF's unzoomed page space (base
 * width = 700px, matching the signing-field system) so overlays survive zoom
 * changes and match between reviewer and author.
 *
 * DsProvider contract — this component reads its services from context:
 *   - `services.review` (author mode, session-authed)
 *   - `services.reviewPublic` (reviewer mode, token-scoped)
 *   - `translate` for i18n copy
 *   - `notify` for non-fatal error toasts
 */
import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import type React from 'react';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDsServices, useDsTranslate, useDsNotify } from '../context/DsProvider';
import '../utils/pdfWorkerSetup';
import { Button } from './ui/Button';
import type {
  AnnotationRect,
  AnnotationType,
  ReviewAnnotation,
  ReviewAnnotationComment,
  ReviewDocumentVersion,
} from '../types/review';
import styles from './PdfReviewViewer.module.scss';

const BASE_WIDTH = 700;
const NOTE_WIDTH = 28;
const NOTE_HEIGHT = 28;

const DEFAULT_FILE_BASE = '/api/file-management-service';
const defaultResolveFileUrl = (fileId: string) => `${DEFAULT_FILE_BASE}/v1/files/${fileId}`;

type Tool = 'select' | 'highlight' | 'note';

export interface PdfReviewViewerProps {
  /**
   * Who is looking, which decides what they may do.
   *
   * - `reviewer` — an EXTERNAL reviewer on a link. Creates, edits and deletes their own
   *   annotations through the token routes.
   * - `reviewer-internal` — a SIGNED-IN reviewer (WP-14961). The same markup tools, through
   *   the session routes, authorised by their reviewer seat. Needs `reviewId`, not `token`.
   * - `author` — Workpay's side, which owns the wording and approves the result. Marks up
   *   like everyone else, AND resolves annotations and replaces the file.
   *
   * ⚠ Markup is not what separates these modes — every mode has it. What `author` adds is the
   * FILE actions (Replace PDF, Edit Values) and Resolve. Read the difference through the
   * predicates below rather than comparing to a literal: gating markup on `mode === 'reviewer'`
   * is what left a signed-in reviewer with a read-only screen to begin with.
   */
  mode: 'reviewer' | 'reviewer-internal' | 'author';
  /**
   * Is this caller the OWNING side of the request — the side that approves the result?
   *
   * Workpay on a Workpay-owned request, the client's own admins on a client-owned one; the
   * backend computes it (`EnvelopeApprovalPolicy.mayDecideChanges`) and the host passes its
   * answer through. It gates the file actions (Replace PDF, Edit Values) and Resolve, every
   * one of which is served by a route carrying `requireOwnerToChangeDocument` — so deriving
   * them from `mode === 'author'` instead renders buttons that 403 for anyone looking at a
   * request they do not own.
   *
   * Defaults to `mode === 'author'`, which is the old behaviour and is correct for a host
   * that only ever mounts author mode for the owner.
   */
  isOwner?: boolean;
  /** Required in author and reviewer-internal mode. */
  reviewId?: string;
  /** Required in reviewer mode — used for all token-scoped calls. */
  token?: string;
  /** Scopes the viewer to a specific document within a multi-doc review. Optional. */
  reviewDocumentId?: string;
  /** Current user's reviewer id (reviewer mode) so we can distinguish "own" annotations. */
  currentReviewerId?: string;
  /**
   * Resolves a blob fileId to a URL react-pdf can fetch. Consumers typically
   * pass `(id) => fileManagementClient.getFileUrl(id)` from their shared file
   * service. Defaults to `/api/file-management-service/v1/files/{fileId}` if
   * omitted.
   */
  resolveFileUrl?: (fileId: string) => string;
  /** Optional callback after author replaces the underlying PDF file. */
  onFileReplaced?: () => void;
  /**
   * All versions of this document (ascending by version number). The viewer renders the latest
   * by default with a version switcher in the toolbar. Annotations are filtered to the selected
   * version so v2 starts with a clean canvas while v1 stays viewable.
   */
  versions: ReviewDocumentVersion[];
  /**
   * The PDF has settled — rendered, or definitively failed.
   *
   * Lets a host gate its review actions on the document being on screen (WP-14961 demo
   * feedback). Fires on the failure path too: the actions are the reviewer's only way off
   * the screen, so a document that will never arrive must not hold them disabled forever.
   */
  onReady?: () => void;
}

export function PdfReviewViewer({
  mode,
  isOwner,
  reviewId,
  token,
  reviewDocumentId,
  currentReviewerId,
  resolveFileUrl = defaultResolveFileUrl,
  onFileReplaced,
  versions,
  onReady,
}: PdfReviewViewerProps) {
  const services = useDsServices();
  const t = useDsTranslate();
  const notify = useDsNotify();
  const reviewApi = services.review;
  const reviewPublicApi = services.reviewPublic;

  /**
   * Writes go through the token routes, or the session ones. The ONLY thing the external
   * reviewer does differently — everything about what they may do is the same.
   */
  const byToken = mode === 'reviewer';
  /** The owning side. See {@link PdfReviewViewerProps.isOwner}. */
  const owner = isOwner ?? mode === 'author';
  /**
   * May mark the document up. A reviewer always may — it IS their review. The owning side may
   * because they own the wording and approve the result. Anyone else is reading: the create
   * route falls through to the owner gate for them, so showing the tools would be a 403 with
   * extra steps.
   */
  const canAnnotate = mode !== 'author' || owner;

  const queryClient = useQueryClient();
  const containerRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState(0);
  const [zoom, setZoom] = useState(1);
  /**
   * Has the reader set the zoom themselves? Once they have, stop re-fitting on resize — a
   * viewer that keeps overriding a chosen zoom is worse than one that never fits.
   */
  const zoomTouchedRef = useRef(false);
  const [tool, setTool] = useState<Tool>('highlight');

  /**
   * Open at fit width (2026-11-02, demo feedback: "the document should be fit to width by
   * default always everywhere").
   *
   * The viewer rasterises every page at a fixed `BASE_WIDTH` of 700px and scales with CSS, so
   * at zoom 1 the contract was 700px wide regardless of the frame — too small to read in a
   * wide window, and overflowing a narrow one. Measured rather than assumed: the scroll area's
   * padding is a rem-based token, so a hardcoded inset would be wrong at any root font size
   * other than the default.
   *
   * Capped at 1.5 for the AUTOMATIC fit. The zoom-in button still reaches 2 — this bounds
   * what we choose for the reader, not what they can choose.
   *
   * Why not the button's full 2: every page is rendered eagerly, with no virtualisation, and
   * canvas memory scales with the square of the width — so defaulting a wide pane to 2 would
   * roughly quadruple it against the old behaviour on a long contract. 1.5 is ~2.25×, reached
   * only on a pane wider than 1050px, and still fixes the actual bug, which was that the fit
   * could never grow at all. Revisit with a measurement on a 30-page contract, or when the
   * page list is virtualised. (Re-review, 2026-11-02.)
   *
   * It was 1, on the reasoning that a 700px bitmap magnified past its own resolution goes
   * soft. That is true of `TemplateBuilder`, which scales a fixed-width `PdfViewer` with CSS
   * `zoom`, and false here: this viewer passes `width={BASE_WIDTH * zoom}` straight to
   * react-pdf's `<Page>` (below), which rasterises AT that width. So there was no resolution
   * to protect, and in the very case the cap was written for — a contract beside a panel in
   * a wide window — `inner > BASE_WIDTH` clamped back to 1 and nothing changed at all. The
   * fit only ever shrank. (Review finding, 2026-11-02.)
   *
   * `Math.floor`, not round — rounding up by a fraction of a pixel overflows the box, raises a
   * horizontal scrollbar, and on a platform with classic scrollbars that changes `clientWidth`
   * and re-fires this.
   */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const fit = () => {
      if (zoomTouchedRef.current) return;
      const cs = getComputedStyle(el);
      const inner =
        el.clientWidth - parseFloat(cs.paddingLeft || '0') - parseFloat(cs.paddingRight || '0');
      if (inner <= 0) return;
      const next = Math.min(1.5, Math.floor((inner / BASE_WIDTH) * 100) / 100);
      if (next > 0) setZoom((z) => (Math.abs(z - next) < 0.005 ? z : next));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Default the version switcher to the latest. When the parent appends a new version
  // (after author upload) we auto-jump to it so the new revision is shown immediately.
  const latestVersion = versions.length > 0 ? versions[versions.length - 1]! : null;
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(latestVersion?.id ?? null);
  useEffect(() => {
    if (latestVersion && (!selectedVersionId || !versions.some((v) => v.id === selectedVersionId))) {
      setSelectedVersionId(latestVersion.id);
    } else if (latestVersion && selectedVersionId) {
      const currentIsLatest = versions[versions.length - 1]?.id === selectedVersionId;
      const isFollowingHead =
        !currentIsLatest &&
        versions.findIndex((v) => v.id === selectedVersionId) === versions.length - 2;
      if (isFollowingHead) setSelectedVersionId(latestVersion.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versions.length, latestVersion?.id]);

  const selectedVersion = versions.find((v) => v.id === selectedVersionId) ?? latestVersion;
  const fileUrl = selectedVersion ? resolveFileUrl(selectedVersion.fileId) : '';
  const isViewingLatest = selectedVersion?.id === latestVersion?.id;

  const [pendingNote, setPendingNote] = useState<{ page: number; x: number; y: number } | null>(null);
  const [showEditValues, setShowEditValues] = useState(false);
  const [noteMessage, setNoteMessage] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const queryKey = useMemo(
    () => (byToken ? ['ds-review-annotations-token', token] : ['ds-review-annotations', reviewId]),
    [byToken, token, reviewId],
  );

  const { data: allAnnotations = [] } = useQuery({
    queryKey,
    queryFn: async (): Promise<ReviewAnnotation[]> => {
      if (byToken && token && reviewPublicApi) return reviewPublicApi.listAnnotationsByToken(token);
      if (!byToken && reviewId && reviewApi) return reviewApi.listAnnotations(reviewId);
      return [];
    },
    enabled: (byToken && !!token && !!reviewPublicApi) || (!byToken && !!reviewId && !!reviewApi),
  });

  const annotations = useMemo(() => {
    let list = reviewDocumentId
      ? allAnnotations.filter((a) => a.reviewDocumentId === reviewDocumentId)
      : allAnnotations;
    if (selectedVersionId) {
      list = list.filter((a) =>
        a.reviewDocumentVersionId
          ? a.reviewDocumentVersionId === selectedVersionId
          : selectedVersion?.versionNumber === 1,
      );
    }
    return list;
  }, [allAnnotations, reviewDocumentId, selectedVersionId, selectedVersion?.versionNumber]);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  const createMutation = useMutation({
    mutationFn: (annotation: Partial<ReviewAnnotation>) => {
      if (!canAnnotate) throw new Error('Cannot add an annotation here');
      if (!isViewingLatest) throw new Error('Switch to the latest version to add annotations');
      const body = {
        ...annotation,
        reviewDocumentId,
        reviewDocumentVersionId: selectedVersion?.id,
      };
      if (byToken) {
        if (!token || !reviewPublicApi) throw new Error('Cannot add an annotation here');
        return reviewPublicApi.createAnnotation(token, body);
      }
      if (!reviewId || !reviewApi) throw new Error('Cannot add an annotation here');
      // Two session routes, because the two sides are authorised differently: a reviewer by
      // their seat (permission-free), the owning side by permission plus the owner gate.
      return mode === 'reviewer-internal'
        ? reviewApi.createReviewerAnnotation(reviewId, body)
        : reviewApi.createAnnotation(reviewId, body);
    },
    onSuccess: (created) => {
      invalidate();
      if (created?.id) setExpandedId(created.id);
    },
    onError: (err: Error) => {
      notify({ variant: 'critical', title: err.message });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (annotationId: string) => {
      if (mode === 'reviewer' && token && reviewPublicApi) {
        return reviewPublicApi.deleteAnnotationAsReviewer(token, annotationId);
      }
      if (reviewId && reviewApi) {
        return mode === 'reviewer-internal'
          ? reviewApi.deleteReviewerAnnotation(reviewId, annotationId)
          : reviewApi.deleteAnnotationAsAuthor(reviewId, annotationId);
      }
      throw new Error('Cannot delete annotation in current mode');
    },
    onSuccess: invalidate,
    // Every mutation here reports its own failure. Three of them used to fail in silence, so
    // a refused delete looked exactly like a click that missed.
    onError: (err: Error) => notify({ variant: 'critical', title: err.message }),
  });

  const resolveMutation = useMutation({
    mutationFn: (annotationId: string) => {
      if (!owner || !reviewId || !reviewApi) throw new Error('Only the owning side can resolve annotations');
      return reviewApi.resolveAnnotation(reviewId, annotationId);
    },
    onSuccess: invalidate,
    onError: (err: Error) => notify({ variant: 'critical', title: err.message }),
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const replaceMutation = useMutation({
    mutationFn: (file: File) => {
      if (!owner || !reviewId || !reviewApi) throw new Error('Only the owning side can replace the document');
      return reviewApi.replaceReviewDocument(reviewId, file, reviewDocumentId);
    },
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['ds-reviews'], exact: false });
      onFileReplaced?.();
    },
    onError: (err: Error) => {
      notify({ variant: 'critical', title: err.message });
    },
  });

  const handleDocumentLoad = useCallback(
    (pdf: { numPages: number }) => {
      setNumPages(pdf.numPages);
      setLoadError(null);
      onReady?.();
    },
    [onReady],
  );

  const handlePageMouseUp = useCallback(
    (e: React.MouseEvent<HTMLDivElement>, pageNumber: number) => {
      if (!canAnnotate) return;
      // An annotation belongs to the revision it was drawn on, so marks cannot be added to an
      // older one.
      if (!isViewingLatest) return;
      if (tool === 'note') {
        const pageEl = e.currentTarget;
        const rect = pageEl.getBoundingClientRect();
        const x = (e.clientX - rect.left) / zoom - NOTE_WIDTH / 2;
        const y = (e.clientY - rect.top) / zoom - NOTE_HEIGHT / 2;
        setPendingNote({ page: pageNumber, x: Math.max(0, x), y: Math.max(0, y) });
        setNoteMessage('');
        return;
      }

      if (tool !== 'highlight') return;
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || selection.rangeCount === 0) return;

      const pageEl = e.currentTarget;
      const anchor = selection.anchorNode;
      if (!anchor || !pageEl.contains(anchor as Node)) return;

      const range = selection.getRangeAt(0);
      const pageRect = pageEl.getBoundingClientRect();
      const clientRects = Array.from(range.getClientRects());
      if (clientRects.length === 0) return;

      const localRects: AnnotationRect[] = clientRects
        .map((r) => ({
          x: (r.left - pageRect.left) / zoom,
          y: (r.top - pageRect.top) / zoom,
          width: r.width / zoom,
          height: r.height / zoom,
        }))
        .filter((r) => r.width > 1 && r.height > 1);
      if (localRects.length === 0) return;

      const highlightedText = selection.toString();
      const bbox = boundingBox(localRects);

      createMutation.mutate({
        annotationType: 'HIGHLIGHT' satisfies AnnotationType,
        pageNumber,
        xPosition: bbox.x,
        yPosition: bbox.y,
        width: bbox.width,
        height: bbox.height,
        rectsJson: JSON.stringify(localRects),
        highlightedText,
        color: '#ffd54f',
      });

      selection.removeAllRanges();
    },
    // `canAnnotate`, not `mode`/`token`: the toolbar's visibility is a render expression and
    // reacts to `isOwner` at once, but this closure would not — a view→edit transition does not
    // remount the viewer, so a stale `false` here leaves the buttons visible and inert.
    [canAnnotate, tool, zoom, isViewingLatest, createMutation],
  );

  const submitPendingNote = useCallback(() => {
    if (!pendingNote) return;
    createMutation.mutate({
      annotationType: 'NOTE' satisfies AnnotationType,
      pageNumber: pendingNote.page,
      xPosition: pendingNote.x,
      yPosition: pendingNote.y,
      width: NOTE_WIDTH,
      height: NOTE_HEIGHT,
      message: noteMessage.trim() || undefined,
      color: '#fbbf24',
    });
    setPendingNote(null);
    setNoteMessage('');
    setTool('select');
  }, [pendingNote, noteMessage, createMutation]);

  /**
   * Mine, as opposed to the other side's. A reviewer's annotations carry their seat id; the
   * owning side's carry none at all, which is exactly how the server files them.
   */
  const isOwnAnnotation = useCallback(
    (a: ReviewAnnotation) =>
      mode === 'author'
        ? !a.reviewerId
        : !!currentReviewerId && a.reviewerId === currentReviewerId,
    [mode, currentReviewerId],
  );

  /**
   * Who may remove an annotation: whoever made it. A reviewer's own goes through their
   * seat-authorised route, the owning side's through the author route behind the owner gate.
   */
  const canEditAnnotation = useCallback(
    (a: ReviewAnnotation) => (mode === 'author' ? owner : true) && isOwnAnnotation(a),
    [mode, owner, isOwnAnnotation],
  );

  if (!fileUrl) {
    return <div className={styles.placeholder}>{t('K_DS_PDF_REVIEW_LOADING', 'Loading document\u2026')}</div>;
  }

  return (
    <div className={styles.viewer}>
      <div className={styles.toolbar}>
        {/* Markup is for every side that can act on this contract — reviewers because it IS
            their review, the owning side because they own the wording and approve the result.
            The block below is the FILE actions, which is a different question from "may I say
            something about this passage". */}
        {canAnnotate && (
          <div className={styles.toolbarGroup}>
            <button
              className={`${styles.toolBtn} ${tool === 'select' ? styles.toolBtnActive : ''}`}
              onClick={() => setTool('select')}
              title={t('K_DS_PDF_REVIEW_TOOL_SELECT', 'Select and view annotations')}
            >
              {t('K_DS_PDF_REVIEW_TOOL_SELECT_LABEL', '\u2196 Select')}
            </button>
            <button
              className={`${styles.toolBtn} ${tool === 'highlight' ? styles.toolBtnActive : ''}`}
              onClick={() => setTool('highlight')}
              disabled={!isViewingLatest}
              title={
                isViewingLatest
                  ? t('K_DS_PDF_REVIEW_TOOL_HIGHLIGHT', 'Select text to highlight')
                  : t('K_DS_PDF_REVIEW_TOOL_HIGHLIGHT_DISABLED', 'Switch to the latest version to highlight')
              }
            >
              {t('K_DS_PDF_REVIEW_TOOL_HIGHLIGHT_LABEL', '\uD83D\uDD8D Highlight')}
            </button>
            <button
              className={`${styles.toolBtn} ${tool === 'note' ? styles.toolBtnActive : ''}`}
              onClick={() => setTool('note')}
              disabled={!isViewingLatest}
              title={
                isViewingLatest
                  ? t('K_DS_PDF_REVIEW_TOOL_NOTE', 'Click on the document to drop a note')
                  : t('K_DS_PDF_REVIEW_TOOL_NOTE_DISABLED', 'Switch to the latest version to add notes')
              }
            >
              {t('K_DS_PDF_REVIEW_TOOL_NOTE_LABEL', '\uD83D\uDCCC Note')}
            </button>
          </div>
        )}

        {versions.length > 1 && (
          <div className={styles.versionSwitcher}>
            <label className={styles.versionLabel}>
              {t('K_DS_PDF_REVIEW_VERSION', 'Version')}
            </label>
            <select
              className={styles.versionSelect}
              value={selectedVersionId ?? ''}
              onChange={(e) => setSelectedVersionId(e.target.value || null)}
            >
              {versions.map((v) => (
                <option key={v.id} value={v.id}>
                  {`v${v.versionNumber}${v.id === latestVersion?.id ? ` ${t('K_DS_VERSION_LATEST', '(latest)')}` : ''}${v.uploadedAt ? ` \u2014 ${new Date(v.uploadedAt).toLocaleDateString()}` : ''}`}
                </option>
              ))}
            </select>
          </div>
        )}

        {owner && (
          <div className={styles.toolbarGroup}>
            <button
              className={styles.toolBtn}
              onClick={() => fileInputRef.current?.click()}
              disabled={replaceMutation.isPending}
              title={t('K_DS_PDF_REVIEW_REPLACE', 'Upload a revised PDF to replace the current document')}
            >
              {replaceMutation.isPending
                ? t('K_DS_PDF_REVIEW_UPLOADING', 'Uploading\u2026')
                : t('K_DS_PDF_REVIEW_REPLACE_LABEL', '\uD83D\uDCE4 Replace PDF')}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) replaceMutation.mutate(file);
                e.target.value = '';
              }}
            />
            <button
              className={styles.toolBtn}
              onClick={() => setShowEditValues(true)}
              title={t('K_DS_PDF_REVIEW_EDIT_VALUES', 'Update author-prefilled values (creates a new version)')}
            >
              {t('K_DS_PDF_REVIEW_EDIT_VALUES_LABEL', '\u270F\uFE0F Edit Values')}
            </button>
          </div>
        )}

        <div className={styles.toolbarDivider} />

        <div className={styles.zoomControls}>
          <button
            className={styles.zoomBtn}
            onClick={() => {
              zoomTouchedRef.current = true;
              setZoom((z) => Math.max(0.5, z - 0.1));
            }}
            title={t('K_DS_PDF_REVIEW_ZOOM_OUT', 'Zoom out')}
          >
            &minus;
          </button>
          <span>{Math.round(zoom * 100)}%</span>
          <button
            className={styles.zoomBtn}
            onClick={() => {
              zoomTouchedRef.current = true;
              setZoom((z) => Math.min(2, z + 0.1));
            }}
            title={t('K_DS_PDF_REVIEW_ZOOM_IN', 'Zoom in')}
          >
            +
          </button>
        </div>

        <span className={styles.count}>
          {annotations.length}{' '}
          {annotations.length === 1
            ? t('K_DS_PDF_REVIEW_ANNOTATION_ONE', 'annotation')
            : t('K_DS_PDF_REVIEW_ANNOTATION_MANY', 'annotations')}
        </span>
      </div>

      <div className={styles.scrollArea} ref={containerRef}>
        <Document
          file={fileUrl}
          onLoadSuccess={handleDocumentLoad}
          onLoadError={(err) => {
            setLoadError(err.message || 'Failed to load PDF');
            onReady?.();
          }}
          loading={<div className={styles.loading}>{t('K_DS_PDF_REVIEW_LOADING_PDF', 'Loading PDF\u2026')}</div>}
          error={
            <div className={styles.error}>
              <p>{t('K_DS_PDF_LOAD_FAILED', 'Failed to load PDF')}</p>
              {loadError && <p>{loadError}</p>}
            </div>
          }
        >
          {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => {
            const pageAnnotations = annotations.filter((a) => a.pageNumber === pageNum);
            return (
              <div key={pageNum} className={styles.pageWrapper}>
                <div
                  className={styles.pageInner}
                  style={{ width: BASE_WIDTH * zoom }}
                  data-page-number={pageNum}
                  onMouseUp={(e) => handlePageMouseUp(e, pageNum)}
                >
                  <Page
                    pageNumber={pageNum}
                    width={BASE_WIDTH * zoom}
                    renderTextLayer={true}
                    renderAnnotationLayer={false}
                    loading={<div className={styles.loading}>{t('K_DS_PDF_REVIEW_RENDERING_PAGE', 'Rendering page')} {pageNum}\u2026</div>}
                  />

                  <div
                    className={styles.overlay}
                    style={{
                      cursor: tool === 'note' ? 'crosshair' : tool === 'highlight' ? 'text' : 'default',
                      pointerEvents: tool === 'select' ? 'none' : 'auto',
                    }}
                  />
                  {pageAnnotations.map((a) => (
                    <AnnotationView
                      key={a.id}
                      annotation={a}
                      zoom={zoom}
                      mode={mode}
                      owner={owner}
                      canEdit={canEditAnnotation(a)}
                      isOwnAnnotation={isOwnAnnotation(a)}
                      reviewId={reviewId}
                      token={token}
                      expanded={expandedId === a.id}
                      onToggleExpand={() =>
                        setExpandedId((prev) => (prev === a.id ? null : a.id))
                      }
                      onResolve={() => resolveMutation.mutate(a.id)}
                      onDelete={() => deleteMutation.mutate(a.id)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </Document>
      </div>

      {pendingNote && (
        <div className={styles.modalBackdrop} onClick={() => setPendingNote(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3>{t('K_DS_PDF_REVIEW_ADD_NOTE_TITLE', 'Add note')}</h3>
            <textarea
              className={styles.modalTextarea}
              value={noteMessage}
              onChange={(e) => setNoteMessage(e.target.value)}
              placeholder={t('K_DS_PDF_REVIEW_NOTE_PLACEHOLDER', 'Describe the issue or suggestion\u2026')}
              autoFocus
              rows={4}
            />
            <div className={styles.modalActions}>
              <Button variant="secondary" size="sm" onClick={() => setPendingNote(null)}>
                {t('K_CANCEL', 'Cancel')}
              </Button>
              <Button size="sm" onClick={submitPendingNote} loading={createMutation.isPending}>
                {t('K_DS_PDF_REVIEW_SAVE_NOTE', 'Save note')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showEditValues && owner && reviewId && (
        <EditAuthorValuesModal
          reviewId={reviewId}
          reviewDocumentId={reviewDocumentId}
          onClose={() => setShowEditValues(false)}
        />
      )}
    </div>
  );
}

// ── Annotation rendering ─────────────────────────────────────────────

interface AnnotationViewProps {
  annotation: ReviewAnnotation;
  zoom: number;
  /** Same three-way as {@link PdfReviewViewerProps.mode}. */
  mode: 'reviewer' | 'reviewer-internal' | 'author';
  /** Same as {@link PdfReviewViewerProps.isOwner}, already resolved. */
  owner: boolean;
  canEdit: boolean;
  isOwnAnnotation: boolean;
  reviewId?: string;
  token?: string;
  expanded: boolean;
  onToggleExpand: () => void;
  onResolve: () => void;
  onDelete: () => void;
}

function AnnotationView({
  annotation,
  zoom,
  mode,
  owner,
  canEdit,
  isOwnAnnotation,
  reviewId,
  token,
  expanded,
  onToggleExpand,
  onResolve,
  onDelete,
}: AnnotationViewProps) {
  const resolved = !!annotation.resolvedAt;
  const RESOLVED_COLOR = '#22c55e';
  const color = resolved
    ? RESOLVED_COLOR
    : annotation.color ?? (annotation.annotationType === 'HIGHLIGHT' ? '#ffd54f' : '#fbbf24');
  const baseStyle: React.CSSProperties = { opacity: resolved ? 0.85 : 1 };

  if (annotation.annotationType === 'HIGHLIGHT') {
    const rects = parseRects(annotation.rectsJson);
    return (
      <>
        {rects.map((r, i) => (
          <div
            key={i}
            className={styles.highlightRect}
            style={{
              left: r.x * zoom,
              top: r.y * zoom,
              width: r.width * zoom,
              height: r.height * zoom,
              backgroundColor: `${color}66`,
              borderColor: color,
              ...baseStyle,
            }}
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
          />
        ))}
        {expanded && (
          <AnnotationDetails
            annotation={annotation}
            top={(annotation.yPosition ?? 0) * zoom + (annotation.height ?? 0) * zoom + 4}
            left={(annotation.xPosition ?? 0) * zoom}
            mode={mode}
            owner={owner}
            canEdit={canEdit}
            isOwnAnnotation={isOwnAnnotation}
            reviewId={reviewId}
            token={token}
            onResolve={onResolve}
            onDelete={onDelete}
            onClose={onToggleExpand}
          />
        )}
      </>
    );
  }

  const x = (annotation.xPosition ?? 0) * zoom;
  const y = (annotation.yPosition ?? 0) * zoom;
  return (
    <>
      <button
        className={styles.notePin}
        style={{ left: x, top: y, backgroundColor: color, ...baseStyle }}
        title={annotation.message ?? 'Note'}
        onClick={(e) => {
          e.stopPropagation();
          onToggleExpand();
        }}
      >
        {resolved ? '\u2713' : '\uD83D\uDCCC'}
      </button>
      {expanded && (
        <AnnotationDetails
          annotation={annotation}
          top={y + NOTE_HEIGHT + 4}
          left={x}
          mode={mode}
          owner={owner}
          canEdit={canEdit}
          isOwnAnnotation={isOwnAnnotation}
          reviewId={reviewId}
          token={token}
          onResolve={onResolve}
          onDelete={onDelete}
          onClose={onToggleExpand}
        />
      )}
    </>
  );
}

interface AnnotationDetailsProps {
  annotation: ReviewAnnotation;
  top: number;
  left: number;
  /** Same three-way as {@link PdfReviewViewerProps.mode}. */
  mode: 'reviewer' | 'reviewer-internal' | 'author';
  /** Same as {@link PdfReviewViewerProps.isOwner}, already resolved. */
  owner: boolean;
  canEdit: boolean;
  isOwnAnnotation: boolean;
  token?: string;
  reviewId?: string;
  onResolve: () => void;
  onDelete: () => void;
  onClose: () => void;
}

function AnnotationDetails({
  annotation,
  top,
  left,
  mode,
  owner,
  canEdit,
  isOwnAnnotation,
  token,
  reviewId,
  onResolve,
  onDelete,
  onClose,
}: AnnotationDetailsProps) {
  const services = useDsServices();
  const t = useDsTranslate();
  const notify = useDsNotify();
  const reviewApi = services.review;
  const reviewPublicApi = services.reviewPublic;
  const queryClient = useQueryClient();

  /** Token-scoped reads and writes, or session-scoped ones. Mirrors the viewer's own split. */
  const byToken = mode === 'reviewer';
  /**
   * May this viewer post into the thread? Anyone who can act on the contract: a reviewer
   * through their seat-authorised route, the owning side through the author route. A
   * signed-in reader of someone else's request may not — the author route would refuse them,
   * so the thread stays read-only rather than offering a Post that 403s.
   */
  const canComment = mode !== 'author' || owner;

  const commentsKey = useMemo(
    () =>
      byToken
        ? ['ds-review-annotation-comments-token', token, annotation.id]
        : ['ds-review-annotation-comments', reviewId, annotation.id],
    [byToken, token, reviewId, annotation.id],
  );

  const { data: comments = [] } = useQuery({
    queryKey: commentsKey,
    queryFn: async (): Promise<ReviewAnnotationComment[]> => {
      if (byToken && token && reviewPublicApi) {
        return reviewPublicApi.listAnnotationCommentsByToken(token, annotation.id);
      }
      if (!byToken && reviewId && reviewApi) {
        return reviewApi.listAnnotationComments(reviewId, annotation.id);
      }
      return [];
    },
    enabled:
      (byToken && !!token && !!reviewPublicApi) || (!byToken && !!reviewId && !!reviewApi),
  });

  const [draft, setDraft] = useState('');
  useEffect(() => {
    setDraft('');
  }, [annotation.id]);

  const addMutation = useMutation({
    mutationFn: (message: string) => {
      if (mode === 'reviewer' && token && reviewPublicApi) {
        return reviewPublicApi.addAnnotationCommentByToken(token, annotation.id, message);
      }
      if (reviewId && reviewApi) {
        return mode === 'reviewer-internal'
          ? reviewApi.addReviewerAnnotationComment(reviewId, annotation.id, message)
          : reviewApi.addAnnotationComment(reviewId, annotation.id, message);
      }
      throw new Error('Cannot post comment in current mode');
    },
    onSuccess: () => {
      setDraft('');
      queryClient.invalidateQueries({ queryKey: commentsKey });
    },
    // Same reason as every other mutation here: a refused Post must not look like a click
    // that missed. `draft` is deliberately left intact, so the words survive the refusal.
    onError: (err: Error) => notify({ variant: 'critical', title: err.message }),
  });

  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: string) => {
      if (mode === 'reviewer' && token && reviewPublicApi) {
        return reviewPublicApi.deleteAnnotationCommentAsReviewer(token, commentId);
      }
      if (mode === 'author' && reviewId && reviewApi) {
        return reviewApi.deleteAnnotationCommentAsAuthor(reviewId, commentId);
      }
      throw new Error('Cannot delete comment in current mode');
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: commentsKey }),
    onError: (err: Error) => notify({ variant: 'critical', title: err.message }),
  });

  const postComment = useCallback(() => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    addMutation.mutate(trimmed);
  }, [draft, addMutation]);

  const resolved = !!annotation.resolvedAt;
  const currentRole: 'AUTHOR' | 'REVIEWER' = mode === 'author' ? 'AUTHOR' : 'REVIEWER';

  return (
    <div
      className={`${styles.detailsCard} ${resolved ? styles.detailsCardResolved : ''}`}
      style={{ top, left }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className={styles.detailsHeader}>
        <span className={styles.detailsType}>
          {annotation.annotationType === 'HIGHLIGHT'
            ? t('K_DS_PDF_REVIEW_TOOL_HIGHLIGHT_LABEL', '\uD83D\uDD8D Highlight')
            : t('K_DS_PDF_REVIEW_TOOL_NOTE_LABEL', '\uD83D\uDCCC Note')}
        </span>
        {resolved && (
          <span className={styles.detailsResolved}>
            {t('K_DS_PDF_REVIEW_RESOLVED', '\u2713 Resolved')}
          </span>
        )}
        <button className={styles.detailsClose} onClick={onClose}>
          &times;
        </button>
      </div>
      {annotation.annotationType === 'HIGHLIGHT' && annotation.highlightedText && (
        <blockquote className={styles.detailsQuote}>"{annotation.highlightedText}"</blockquote>
      )}

      <div className={styles.thread}>
        {comments.length === 0 && (
          <div className={styles.threadEmpty}>
            {t('K_DS_PDF_REVIEW_NO_COMMENTS', 'No comments yet.')}
          </div>
        )}
        {comments.map((c) => {
          /**
           * ⚠ Role equality, not identity: with several writers on one side this says "my
           * side wrote it", not "I wrote it". It is also gated on there BEING a delete route
           * for this viewer — there is no session reviewer delete-comment route, and the
           * author one carries the owner gate — because an ungated ✕ threw on click and said
           * nothing. A signed-in reviewer can therefore post into a thread and not unpost;
           * recorded in FEATURE.md as the remaining gap.
           */
          const canDeleteComment = mode === 'reviewer' || (mode === 'author' && owner);
          const own =
            canDeleteComment &&
            ((currentRole === 'REVIEWER' && c.authorType === 'REVIEWER') ||
              (currentRole === 'AUTHOR' && c.authorType === 'AUTHOR'));
          return (
            <div
              key={c.id}
              className={`${styles.threadItem} ${
                c.authorType === 'AUTHOR' ? styles.threadItemAuthor : styles.threadItemReviewer
              }`}
            >
              <div className={styles.threadItemMeta}>
                <span className={styles.threadItemRole}>
                  {c.authorType === 'AUTHOR'
                    ? t('K_DS_PDF_REVIEW_ROLE_AUTHOR', 'Author')
                    : t('K_DS_PDF_REVIEW_ROLE_REVIEWER', 'Reviewer')}
                </span>
                {c.authorName && <span> \u2022 {c.authorName}</span>}
                {own && !resolved && (
                  <button
                    className={styles.threadItemDelete}
                    onClick={() => deleteCommentMutation.mutate(c.id)}
                    title={t('K_DS_PDF_REVIEW_DELETE_COMMENT', 'Delete comment')}
                  >
                    &times;
                  </button>
                )}
              </div>
              <div className={styles.threadItemMessage}>{c.message}</div>
            </div>
          );
        })}
      </div>

      {!resolved && canComment && (
        <div className={styles.threadComposer}>
          <textarea
            className={styles.composerInput}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !(e.nativeEvent as KeyboardEvent).isComposing) {
                e.preventDefault();
                postComment();
              }
            }}
            placeholder={
              // Keyed on whose mark this is and whether anyone has written on it yet, not on
              // the mode: Workpay annotates too now, and "Reply to the reviewer" under their
              // own fresh highlight would be addressed to nobody.
              canEdit && comments.length === 0
                ? annotation.annotationType === 'HIGHLIGHT'
                  ? t('K_DS_PDF_REVIEW_COMPOSE_HIGHLIGHT', 'Comment on this highlight\u2026 (Enter to post, Shift+Enter for newline)')
                  : t('K_DS_PDF_REVIEW_COMPOSE_NOTE', 'Describe the issue\u2026 (Enter to post, Shift+Enter for newline)')
                : mode === 'author'
                  ? t('K_DS_PDF_REVIEW_COMPOSE_AUTHOR_REPLY', 'Reply to the reviewer\u2026 (Enter to post)')
                  : t('K_DS_PDF_REVIEW_COMPOSE_REPLY', 'Reply\u2026 (Enter to post)')
            }
            autoFocus={isOwnAnnotation && comments.length === 0}
            rows={2}
          />
          <div className={styles.composerActions}>
            <Button size="sm" onClick={postComment} disabled={!draft.trim() || addMutation.isPending}>
              {addMutation.isPending
                ? t('K_DS_PDF_REVIEW_POSTING', 'Posting\u2026')
                : t('K_DS_PDF_REVIEW_POST', 'Post')}
            </Button>
          </div>
        </div>
      )}

      {(owner || canEdit) && (
        <div className={styles.detailsFooter}>
          {/* Resolve is served by the author route, which carries the owner gate. */}
          {owner && !resolved && (
            <Button variant="secondary" size="sm" onClick={onResolve}>
              {t('K_DS_PDF_REVIEW_RESOLVE_ACTION', '\u2713 Resolve')}
            </Button>
          )}
          <div className={styles.detailsFooterSpacer} />
          {canEdit && (
            <button
              className={styles.detailsDangerBtn}
              onClick={onDelete}
              title={t('K_DS_PDF_REVIEW_DELETE_ANNOTATION', 'Delete annotation')}
            >
              {t('K_DELETE', 'Delete')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Utilities ────────────────────────────────────────────────────────

function boundingBox(rects: AnnotationRect[]): AnnotationRect {
  const x0 = Math.min(...rects.map((r) => r.x));
  const y0 = Math.min(...rects.map((r) => r.y));
  const x1 = Math.max(...rects.map((r) => r.x + r.width));
  const y1 = Math.max(...rects.map((r) => r.y + r.height));
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
}

function parseRects(json?: string): AnnotationRect[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (Array.isArray(parsed)) return parsed as AnnotationRect[];
  } catch {
    // fall through
  }
  return [];
}

// ── Author Edit Values modal ─────────────────────────────────────────

interface EditAuthorValuesModalProps {
  reviewId: string;
  reviewDocumentId?: string;
  onClose: () => void;
}

function EditAuthorValuesModal({ reviewId, reviewDocumentId, onClose }: EditAuthorValuesModalProps) {
  const services = useDsServices();
  const t = useDsTranslate();
  const reviewApi = services.review;
  const queryClient = useQueryClient();

  const { data: fields = [], isLoading } = useQuery({
    queryKey: ['ds-review-author-fields', reviewId, reviewDocumentId ?? 'legacy'],
    queryFn: () => {
      if (!reviewApi) throw new Error('Review service not configured');
      return reviewApi.listAuthorFieldsForReview(reviewId, reviewDocumentId);
    },
    enabled: !!reviewApi,
  });

  const [drafts, setDrafts] = useState<Record<string, string>>({});
  useEffect(() => {
    if (fields.length === 0) return;
    setDrafts((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      const seed: Record<string, string> = {};
      for (const f of fields) if (f.id) seed[f.id] = f.value ?? '';
      return seed;
    });
  }, [fields]);

  const updateMutation = useMutation({
    mutationFn: () => {
      if (!reviewApi) throw new Error('Review service not configured');
      const entries = fields
        .filter((f) => f.id)
        .map((f) => ({ fieldId: f.id!, value: drafts[f.id!] ?? '' }));
      return reviewApi.updateAuthorValues(reviewId, reviewDocumentId, entries);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ds-reviews'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['ds-review-author-fields', reviewId], exact: false });
      onClose();
    },
  });

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      updateMutation.mutate();
    }
  };

  const isDirty = fields.some((f) => f.id && (drafts[f.id] ?? '') !== (f.value ?? ''));

  const inputForField = (f: { id?: string; fieldType: string; label?: string; defaultValue?: string | null }) => {
    if (!f.id) return null;
    const value = drafts[f.id] ?? '';
    const setValue = (v: string) => setDrafts((prev) => ({ ...prev, [f.id!]: v }));
    const placeholder = f.label || f.fieldType;

    if (f.fieldType === 'CHECKBOX') {
      return (
        <label className={styles.editValueCheckbox}>
          <input
            type="checkbox"
            checked={value === 'true'}
            onChange={(e) => setValue(e.target.checked ? 'true' : 'false')}
          />
          {placeholder}
        </label>
      );
    }
    const inputType =
      f.fieldType === 'DATE' || f.fieldType === 'DATE_SIGNED'
        ? 'date'
        : f.fieldType === 'NUMBER' || f.fieldType === 'CURRENCY'
          ? 'number'
          : f.fieldType === 'EMAIL'
            ? 'email'
            : f.fieldType === 'PHONE'
              ? 'tel'
              : 'text';
    return (
      <input
        className={styles.editValueInput}
        type={inputType}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={f.defaultValue || placeholder}
      />
    );
  };

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.editValuesModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.editValuesHeader}>
          <h3>{t('K_DS_AUTHOR_VALUES_EDIT_MODAL_TITLE', 'Edit prefilled values')}</h3>
          <button className={styles.detailsClose} onClick={onClose}>
            &times;
          </button>
        </div>
        <div className={styles.editValuesBody}>
          {isLoading ? (
            <div className={styles.editValuesEmpty}>{t('K_DS_PDF_REVIEW_EDIT_LOADING', 'Loading\u2026')}</div>
          ) : fields.length === 0 ? (
            <div className={styles.editValuesEmpty}>
              {t('K_DS_PDF_REVIEW_EDIT_EMPTY', 'No prefilled fields on this document.')}
            </div>
          ) : (
            <div className={styles.editValuesList}>
              {fields.map((f) => (
                <div key={f.id} className={styles.editValueRow}>
                  <label className={styles.editValueLabel}>
                    {f.label && f.label !== f.fieldType ? f.label : f.fieldType}
                  </label>
                  {inputForField(f)}
                </div>
              ))}
            </div>
          )}
          {updateMutation.isError && (
            <div className={styles.editValuesError}>
              {(updateMutation.error as Error)?.message ?? t('K_DS_PDF_REVIEW_EDIT_SAVE_FAILED', 'Failed to save changes')}
            </div>
          )}
          <p className={styles.editValuesHint}>
            {t('K_DS_PDF_REVIEW_EDIT_HINT', 'Saving creates a new document version. The reviewer\u2019s page updates automatically.')}
          </p>
        </div>
        <div className={styles.modalActions}>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={updateMutation.isPending}>
            {t('K_CANCEL', 'Cancel')}
          </Button>
          <Button
            size="sm"
            onClick={() => updateMutation.mutate()}
            loading={updateMutation.isPending}
            disabled={!isDirty || fields.length === 0}
          >
            {t('K_DS_PDF_REVIEW_EDIT_SAVE', 'Save & create new version')}
          </Button>
        </div>
      </div>
    </div>
  );
}
